import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

// ── Shared ────────────────────────────────────────────────────────────────────

export type LinkedTicketStatus = 'ACTIVE' | 'RESOLVED';

/** Fields common to both GitHub and Jira enriched ticket records. */
interface BaseLinkedTicket {
  /** UUID of the append-only history record; null for legacy pre-history rows. */
  ticketRecordId?: string | null;
  configId:        string;
  fingerprint:     string;
  status:          LinkedTicketStatus;
  activeFindingId: string;
  createdAt:       string;
  resolvedAt:      string | null;
  // Enriched from MonitoringConfig
  resourceName?:   string;
  serviceType?:    string;
  resourceId?:     string;
}

// ── GitHub ────────────────────────────────────────────────────────────────────

export interface LinkedGitHubTicket extends BaseLinkedTicket {
  repository:  string;
  issueNumber: number;
  issueUrl:    string;
}

export interface GitHubTransition {
  id:       string;
  name:     string;
  toStatus: string;
}

// ── Jira ──────────────────────────────────────────────────────────────────────

export interface LinkedJiraTicket extends BaseLinkedTicket {
  cloudId:    string;
  siteUrl:    string;
  projectKey: string;
  issueKey:   string;
  issueId:    string;
  issueUrl:   string;
}

export interface JiraTransition {
  id:       string;
  name:     string;
  toStatus: string;
}

export interface TicketComment {
  id:        string;
  author:    string;
  body:      string;
  createdAt: string;
  url?:      string;
}

// ── Service ───────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class LinkedTicketsService {
  private readonly base = `${environment.apiUrl}/api/v1/tickets`;

  constructor(private readonly http: HttpClient) {}

  // ── GitHub ──────────────────────────────────────────────────────────────────

  listGitHubTickets(): Observable<{ tickets: LinkedGitHubTicket[] }> {
    return this.http.get<{ tickets: LinkedGitHubTicket[] }>(`${this.base}/github`);
  }

  addGitHubComment(
    configId: string,
    fingerprint: string,
    body: string,
  ): Observable<{ commentId?: number; commentUrl?: string; error?: string }> {
    return this.http.post<{ commentId?: number; commentUrl?: string; error?: string }>(
      `${this.base}/github/${configId}/${fingerprint}/comment`,
      { body },
    );
  }

  setGitHubIssueState(
    configId: string,
    fingerprint: string,
    state: 'open' | 'closed',
  ): Observable<{ issueNumber?: number; state?: string; issueUrl?: string; error?: string }> {
    return this.http.patch<{ issueNumber?: number; state?: string; issueUrl?: string; error?: string }>(
      `${this.base}/github/${configId}/${fingerprint}/state`,
      { state },
    );
  }

  getGitHubComments(
    configId: string,
    fingerprint: string,
  ): Observable<{ comments: TicketComment[]; error?: string }> {
    return this.http.get<{ comments: TicketComment[]; error?: string }>(
      `${this.base}/github/${configId}/${fingerprint}/comments`,
    );
  }

  // ── GitHub (history-record routes, keyed by ticketRecordId) ──────────────────

  getGitHubCommentsById(
    ticketRecordId: string,
  ): Observable<{ comments: TicketComment[]; error?: string }> {
    return this.http.get<{ comments: TicketComment[]; error?: string }>(
      `${this.base}/github/history/${ticketRecordId}/comments`,
    );
  }

  addGitHubCommentById(
    ticketRecordId: string,
    body: string,
  ): Observable<{ commentId?: number; commentUrl?: string; error?: string }> {
    return this.http.post<{ commentId?: number; commentUrl?: string; error?: string }>(
      `${this.base}/github/history/${ticketRecordId}/comment`,
      { body },
    );
  }

  setGitHubIssueStateById(
    ticketRecordId: string,
    state: 'open' | 'closed',
  ): Observable<{ issueNumber?: number; state?: string; issueUrl?: string; error?: string }> {
    return this.http.patch<{ issueNumber?: number; state?: string; issueUrl?: string; error?: string }>(
      `${this.base}/github/history/${ticketRecordId}/state`,
      { state },
    );
  }

  // ── Jira ────────────────────────────────────────────────────────────────────

  listJiraTickets(): Observable<{ tickets: LinkedJiraTicket[] }> {
    return this.http.get<{ tickets: LinkedJiraTicket[] }>(`${this.base}/jira`);
  }

  addJiraComment(
    configId: string,
    fingerprint: string,
    body: string,
  ): Observable<{ commentId?: string; error?: string }> {
    return this.http.post<{ commentId?: string; error?: string }>(
      `${this.base}/jira/${configId}/${fingerprint}/comment`,
      { body },
    );
  }

  getJiraTransitions(
    configId: string,
    fingerprint: string,
  ): Observable<{ transitions: JiraTransition[]; error?: string }> {
    return this.http.get<{ transitions: JiraTransition[]; error?: string }>(
      `${this.base}/jira/${configId}/${fingerprint}/transitions`,
    );
  }

  applyJiraTransition(
    configId: string,
    fingerprint: string,
    transitionId: string,
  ): Observable<{ success?: boolean; error?: string }> {
    return this.http.post<{ success?: boolean; error?: string }>(
      `${this.base}/jira/${configId}/${fingerprint}/transition`,
      { transitionId },
    );
  }

  getJiraComments(
    configId: string,
    fingerprint: string,
  ): Observable<{ comments: TicketComment[]; error?: string }> {
    return this.http.get<{ comments: TicketComment[]; error?: string }>(
      `${this.base}/jira/${configId}/${fingerprint}/comments`,
    );
  }

  // ── Jira (history-record routes, keyed by ticketRecordId) ────────────────────

  getJiraCommentsById(
    ticketRecordId: string,
  ): Observable<{ comments: TicketComment[]; error?: string }> {
    return this.http.get<{ comments: TicketComment[]; error?: string }>(
      `${this.base}/jira/history/${ticketRecordId}/comments`,
    );
  }

  addJiraCommentById(
    ticketRecordId: string,
    body: string,
  ): Observable<{ commentId?: string; error?: string }> {
    return this.http.post<{ commentId?: string; error?: string }>(
      `${this.base}/jira/history/${ticketRecordId}/comment`,
      { body },
    );
  }

  getJiraTransitionsById(
    ticketRecordId: string,
  ): Observable<{ transitions: JiraTransition[]; error?: string }> {
    return this.http.get<{ transitions: JiraTransition[]; error?: string }>(
      `${this.base}/jira/history/${ticketRecordId}/transitions`,
    );
  }

  applyJiraTransitionById(
    ticketRecordId: string,
    transitionId: string,
  ): Observable<{ success?: boolean; error?: string }> {
    return this.http.post<{ success?: boolean; error?: string }>(
      `${this.base}/jira/history/${ticketRecordId}/transition`,
      { transitionId },
    );
  }
}
